import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import clsx from 'clsx';
import { useCookies } from 'next-client-cookies';
import { useTheme } from 'next-themes';
import LoadingIcon from 'components/commercetools-ui/atoms/button/loadingIcon';
import {
  decideImageOrColor,
  findExpirationDuration,
  formatDateMMMDDYYYY,
} from 'components/commercetools-ui/organisms/curated-slider/helpers';
import { ActivityDateText, TileType } from 'components/commercetools-ui/organisms/curated-slider/types';
import { fontWeightMap } from 'components/commercetools-ui/organisms/product-category-slider/config';
import { ChangeStoreIcon, RightArrowIcon } from 'components/icons';
import { BLUE_LINK_COLOR_HEX, WHITE_COLOR_HEX } from 'helpers/constants';
import { M2H_SHIPPING_METHOD_KEY } from 'helpers/constants/cart';
import useImageSizes from 'helpers/hooks/useImageSizes';
import useMediaClipThumbnail from 'helpers/hooks/useMediaclipThumbnail';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { getAttributeValueLowerCase } from 'helpers/utils/attributes';
import { getCustomizationData, setCustomizationData } from 'helpers/utils/customizationStorage';
import { desktop, mobile, tablet } from 'helpers/utils/screensizes';
import { cleanString } from 'helpers/utils/util';
import { useRouter } from 'i18n/routing';
import { getLocalizationInfo } from 'project.config';
import { sdk } from 'sdk';
import { Wishlist } from 'types/entity/wishlist';
import { useAccount, useCart, useProjectSettings, useWishlist } from 'frontastic';
import SaveToProjectsComponent from '../save-to-projects';

export interface Props {
  expiresInOrStatus?: string;
  tileCtaText?: string;
  backImage: boolean | undefined;
  backgroundEntity?: string;
  categoryFontweightClass: string | undefined;
  tileTextFontSize?: number;
  expiresInOrStatusFontweight: string | undefined;
  tile: TileType;
  showExpiresIn: boolean | undefined;
  showStatus: boolean | undefined;
  expiresInOrStatusBgColor?: string;
  showSaveToProjects: boolean | undefined;
  isSingleCard?: boolean;
  keyProp?: string | number;
  expiresFontColor?: string;
  ffidCurated?: string;
  sectionClass?: string;
  product?: any;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
  data?: any;
  beautyshot?: string;
  tileImgContainerBgColor?: string;
  activityDateText?: ActivityDateText;
}

const Tile: React.FC<Props> = ({
  tile,
  expiresInOrStatus,
  backImage,
  backgroundEntity,
  tileCtaText,
  categoryFontweightClass,
  tileTextFontSize,
  showExpiresIn,
  expiresInOrStatusFontweight,
  showStatus,
  expiresInOrStatusBgColor,
  showSaveToProjects,
  isSingleCard,
  keyProp,
  expiresFontColor,
  ffidCurated,
  sectionClass,
  product,
  beautyshot,
  tileImgContainerBgColor,
  activityDateText,
}) => {
  const { resolvedTheme = 'light' } = useTheme();
  const router = useRouter();
  const { loggedIn } = useAccount();
  const extensions = sdk.composableCommerce;
  const [themeColor, setThemeColor] = useState('');
  const [isDesktopSize] = useMediaQuery(desktop);
  const { miniCarts } = useCart();
  const [isMobileSize, width] = useMediaQuery(mobile);
  const isPureMobile = width < tablet;
  const { data: wishlist, updateLineItem } = useWishlist();
  const isImageAvailable = tile.mediaClipProjectId || tile?.imageSrc;
  const mediaClipImage = useMediaClipThumbnail(tile.mediaClipProjectId);
  const thumbnail = mediaClipImage.imageSrc || tile?.imageSrc;
  const loading = mediaClipImage.loading;
  const isMediaClipBeautyShot = tile?.item?.custom?.fields?.mediaClipProjectId;
  const mediaClipImagBeautyShot = useMediaClipThumbnail(tile?.item?.custom?.fields?.mediaClipProjectId);
  const thumbnailBeautyShot = mediaClipImagBeautyShot.imageSrc;
  const cookies = useCookies();
  const locale = cookies.get('locale') as string;
  const { region } = getLocalizationInfo(locale);
  const { projectSettings } = useProjectSettings();
  const mediaclipExpireInDays = Number(projectSettings?.mediaclipExpireInDays) || 60;
  useEffect(() => {
    setThemeColor(resolvedTheme ?? '');
  }, [resolvedTheme]);
  const tileImageSizes = useImageSizes({ sm: 0.1, md: 0.1, lg: 0.1, defaultSize: 0.1, maxWidth: 180 });

  const getProjectLastModified = (item?: any): string | undefined => {
    const expirationDate = item?.custom?.fields?.expirationDate;
    const lastModified = expirationDate ? new Date(expirationDate) : null;
    if (!lastModified || Number.isNaN(lastModified.getTime())) return undefined;

    lastModified.setDate(lastModified.getDate() - mediaclipExpireInDays);
    return lastModified.toISOString();
  };

  const projectLastModified = getProjectLastModified(tile?.item);

  const sharedData = async (item?: any, token?: string) => {
    const customizationData = getCustomizationData();
    let orientationValue = '';
    const orientation = getAttributeValueLowerCase(item?.variant?.attributes, 'orientation');

    if (orientation === 'both' || orientation === 'landscape' || orientation === 'horizontal') {
      orientationValue = `${item?.variant?.attributes?.mediaclip_product_id_universal}-L`;
    } else if (orientation === 'portrait' || orientation === 'vertical') {
      orientationValue = `${item?.variant?.attributes?.mediaclip_product_id_universal}-P`;
    } else {
      orientationValue = `${item?.variant?.attributes?.mediaclip_product_id_universal}`;
    }

    const payload = {
      module: item?.variant?.attributes?.mediaclip_module_universal,
      productId: orientationValue,
      themeUrl: item?.attributes?.mediaclip_theme_url_universal || item?.variant?.attributes?.mediaclip_theme_url_universal,
      externalProductId: item?.variant?.sku,
      token: token,
      title: customizationData?.title,
    };
    if (payload?.module && payload?.productId) {
      const res = await extensions.mediaClip.createProject(payload);
      const createdProjectId = res?.isError ? '' : res?.data?.projectId;
      return createdProjectId;
    }
  };

  const getToken = async (item?: any) => {
    if (loggedIn) {
      const resMediaClipToken = await extensions.mediaClip.getUserToken();
      if (!resMediaClipToken.isError && globalThis.window !== undefined) {
        const mediaClipToken = resMediaClipToken?.data?.userToken;
        const rawCustomizationData = getCustomizationData();
        const cart = miniCarts?.find(
          (miniCart) =>
            miniCart.fulfillmentType &&
            (rawCustomizationData?.distributionChannelKey as string)
              ?.toLowerCase()
              ?.includes(miniCart?.fulfillmentType),
        );

        const finalData = {
          ...rawCustomizationData,
          token: mediaClipToken,
          cartId: cart?.cartId ?? '',
          distributionChannelKey: M2H_SHIPPING_METHOD_KEY[region],
          shippingAddress: '',
          shippingMethod: '',
          sku: item?.variant?.sku,
          productName: item?.variant?.attributes?.display_name_universal ?? item?.name,
          categories: [
            item?.variant?.attributes?.level_1_category_universal,
            item?.variant?.attributes?.level_2_category_universal,
            item?.variant?.attributes?.level_3_category_universal,
          ].filter(Boolean),
          type: 'resume',
        };
        setCustomizationData(finalData);
        return mediaClipToken;
      } else {
        console.error('Error fetching user token:', resMediaClipToken.isError);
        return;
      }
    }
  };

  const handleEdit = async (item: any) => {
    const resMediaClipToken = await extensions.mediaClip.getUserToken();
    if (resMediaClipToken && !resMediaClipToken.isError && globalThis.window !== undefined) {

      const nextExpirationDate = new Date();
      nextExpirationDate.setDate(nextExpirationDate.getDate() + mediaclipExpireInDays);

      const updatedCustom = {
        ...item.custom,
        fields: {
          ...item?.custom?.fields,
          expirationDate: nextExpirationDate.toISOString(),
        },
      };

      const updateLineItemPayload = {
        lineItemId: item.lineItemId,
        count: 1,
        custom: updatedCustom,
      };
      await updateLineItem(wishlist as Wishlist, updateLineItemPayload as any, 1);

      if (item?.custom?.fields?.mediaClipProjectId) {
        const existingCustomizationData = getCustomizationData();
        const cart = miniCarts?.find(
          (miniCart) =>
            miniCart.fulfillmentType &&
            (existingCustomizationData?.distributionChannelKey as string)
              ?.toLowerCase()
              ?.includes(miniCart?.fulfillmentType),
        );
        let categories: string[] = [];
        try {
          const productInfo = await extensions.product.getProduct({
            id: item?.productId,
            sku: item?.variant?.sku,
          });
          if (!productInfo.isError) {
            categories =
              productInfo?.data?.categories?.map((cat) => cat.name).filter((name): name is string => Boolean(name)) ??
              [];
          }
        } catch (e) {
          console.error('error while fetching product data', e);
        }
        const updateCustomizationData = {
          ...existingCustomizationData,
          distributionChannelKey: M2H_SHIPPING_METHOD_KEY[region],
          shipping_address: '',
          shipping_method: '',
          cartId: cart?.cartId ?? '',
          type: 'resume',
          categories,
          sku: item?.variant?.sku,
          productName: item?.variant?.attributes?.display_name_universal ?? item?.name,
        };
        setCustomizationData(updateCustomizationData);
        router.push(`/designer/mediaclip/${item?.custom?.fields?.mediaClipProjectId}`);
      }
    }
  };

  const handlePreview = async (item: any) => {
    const token = await getToken(item);

    const createdProjectId = await sharedData(item, token);
    if (createdProjectId) {
      router.push(`/designer/mediaclip/${createdProjectId}`);
    }
  };

  if (
    sectionClass === 'review-projects' &&
    tile?.item?.custom?.fields?.mediaClipProjectId &&
    !mediaClipImagBeautyShot.loading &&
    !mediaClipImagBeautyShot.imageSrc
  ) {
    return null;
  }

  if (sectionClass == 'product-recommend') {
    return (
      <div
        className={`flex justify-between rounded-2xl bg-[#F8F9FA] p-16 lg:p-24 pl-24 lg:pl-32 hover-lift-parent lift-target ${!isDesktopSize && 'mb-20'}`}
        data-testid="curated-tile"
        data-ffid={`ctr-${cleanString(ffidCurated ?? '')}-curated-tile-${keyProp}`}
        style={{ ...decideImageOrColor(backImage, backgroundEntity) }}
      >
        {/* LEFT CONTENT */}
        <div className="flex flex-col justify-between">
          <div>
            {/* Category */}
            <div className="md:mb-16 mb-5 flex items-center gap-8 text-[#018463]">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
              </svg>
              <span
                className="md:text-14 text-12 leading-[1] sm:font-medium"
                data-ffid={`lbl-${cleanString(ffidCurated ?? '')}-category-${keyProp}`}
              >
                {product?.variants?.[0]?.attributes?.short_description_universal || ''}
              </span>
            </div>

            {/* Title */}
            <h3
              className={`line-36 block text-black ${isPureMobile ? 'text-20' : 'text-28'} max-w-200 leading-tight`}
              data-ffid={`lbl-${cleanString(ffidCurated ?? '')}-title-${keyProp}`}
            >
              {product?.name ?? tile?.tileTitle}
            </h3>
          </div>

          {/* Preview LINK like first file */}
          <div className="sm:mt-20 flex flex-col gap-10">
            <button
              type="button"
              className={clsx(`flex items-center pl-2 text-[#0B57D0]`, isPureMobile ? 'text-14' : 'text-20')}
              onClick={() => handlePreview(product)}
              data-ffid={`lnk-${cleanString(ffidCurated ?? '')}-preview-${keyProp}`}
            >
              {tileCtaText || 'Preview'}
              <ChangeStoreIcon className="ml-4 inline-block w-14" />
            </button>
          </div>
        </div>

        {/* RIGHT IMAGE */}
        <div className="ml-20 flex items-center">
          {beautyshot ? (
            <button
              type="button"
              onClick={() => handlePreview(product)}
              className="m-0 cursor-pointer appearance-none border-none bg-transparent text-left overflow-hidden rounded-[20px] p-12"
              style={{ backgroundColor: tileImgContainerBgColor ?? '#F1F3F5' }}
            >
              <Image
                src={beautyshot}
                alt={product?.name ?? ''}
                width={isDesktopSize ? 180 : 120}
                height={isDesktopSize ? 180 : 120}
                className={`${isDesktopSize ? 'size-180 min-w-[180px] min-h-[180px]' : 'size-120 min-w-[120px] min-h-[120px]'} object-contain`}
                data-testid="curated-tile-image"
                data-ffid={`img-${cleanString(ffidCurated ?? '')}-curated-tile-${keyProp}`}
                unoptimized={true}
              />
            </button>
          ) : (
            <div
              className="overflow-hidden rounded-[20px] p-12"
              style={{ backgroundColor: tileImgContainerBgColor ?? '#F1F3F5' }}
            >
              <Image
                src={product?.variants?.[0]?.images?.[0] || tile?.imageSrc}
                alt={product?.name || tile?.imageAlt}
                width={isDesktopSize ? 180 : 120}
                height={isDesktopSize ? 180 : 120}
                className={`${isDesktopSize ? 'size-180 min-w-[180px] min-h-[180px]' : 'size-120 min-w-[120px] min-h-[120px]'} object-contain`}
                data-testid="curated-tile-image"
                data-ffid={`img-${cleanString(ffidCurated ?? '')}-curated-tile-${keyProp}`}
                onClick={() => handlePreview(product)}
                sizes={tileImageSizes}
                unoptimized={true}
              />
            </div>
          )}
        </div>
      </div>
    );
  } else {
    let aspectratio = '';
    if (isMobileSize) {
      aspectratio = sectionClass == 'review-projects' ? 'aspect-[4/3]' : 'aspect-[5/3]';
    } else {
      aspectratio = 'aspect-[1.11/1]';
    }

    return (
      <div
        className={clsx(
          'rounded-xl bg-white hover-lift-parent lift-target sm:rounded-2xl',
          isDesktopSize && 'border-1 group bg-white',
          isSingleCard && '',
          sectionClass === 'past-order' && 'relative',
          sectionClass === 'review-projects' && 'h-390',
        )}
      >
        <div
          style={{
            ...decideImageOrColor(backImage, backgroundEntity),
            ...(isPureMobile && showSaveToProjects ? { border: '1px solid #D6D6D6' } : {}),
          }}
          className={clsx(`h-full rounded-xl px-0 pb-30 sm:rounded-2xl lg:mt-25`)}
          data-testid="curated-tile"
          data-ffid={`ctr-${cleanString(ffidCurated ?? '')}-curated-tile-${keyProp}`}
        >
          <div
            className={`w-full overflow-hidden rounded-xl sm:rounded-2xl ${showSaveToProjects ? 'px-0' : 'p-12 sm:p-24'} relative flex items-center justify-center rounded-xl ${aspectratio}`}
            data-ffid={`ctr-${cleanString(ffidCurated ?? '')}-tile-image-${keyProp}`}
          >
            {showSaveToProjects && isDesktopSize && (
              <SaveToProjectsComponent
                expiresInOrStatus={expiresInOrStatus}
                themeColor={themeColor}
                keyProp={`${keyProp}`}
                ffidCurated={ffidCurated}
              />
            )}
            {isMediaClipBeautyShot && sectionClass == 'review-projects' && (
              <>
                {loading || !thumbnailBeautyShot ? (
                  <div className="flex size-full flex-1 items-center justify-center text-[#333]">
                    <LoadingIcon className="fill-gray-700" />
                  </div>
                ) : (
                  <div className="relative size-full">
                    <Image
                      data-ffid={`img-${cleanString(ffidCurated ?? '')}-curated-tile-${keyProp}`}
                      src={thumbnailBeautyShot}
                      alt="review project thumbnail"
                      fill
                      sizes={tileImageSizes}
                      className="object-contain object-center"
                      unoptimized={true}
                    />
                  </div>
                )}
              </>
            )}
            {isImageAvailable && sectionClass == 'past-order' && (
              <>
                {loading || !thumbnail ? (
                  <div className="flex size-full flex-1 items-center justify-center text-[#333]">
                    <LoadingIcon className="fill-gray-700" />
                  </div>
                ) : (
                  <div className="relative size-full">
                    <Image
                      data-ffid={`img-${cleanString(ffidCurated ?? '')}-curated-tile-${keyProp}`}
                      src={thumbnail}
                      fill
                      sizes={tileImageSizes}
                      alt="Order thumbnail"
                      className="object-contain object-center"
                      unoptimized={true}
                    />
                  </div>
                )}
              </>
            )}
          </div>
          <div
            className="mt-4 text-center sm:mt-0"
            data-ffid={`ctr-${cleanString(ffidCurated ?? '')}-curated-content-${keyProp}`}
          >
            <div
              className="text-14 lg:text-16"
              data-ffid={`lst-${cleanString(ffidCurated ?? '')}-tile-details-${keyProp}`}
            >
              <div
                className={`${categoryFontweightClass}`}
                style={{ fontSize: `${tileTextFontSize || 18}px` }}
                data-ffid={`lbl-${cleanString(ffidCurated ?? '')}-curated-tile-title-${keyProp}`}
              >
                {sectionClass == 'review-projects' && tile.tileTitle && tile.tileTitle.length > 17
                  ? `${tile?.tileTitle?.slice(0, 17)}...`
                  : tile?.tileTitle}{' '}
                {!showSaveToProjects && sectionClass == 'review-projects' && <span>{'-'}</span>}{' '}
                <span data-ffid={`lbl-${cleanString(ffidCurated ?? '')}-curated-tile-date-${keyProp}`}>
                  {tile && !showSaveToProjects && formatDateMMMDDYYYY(((sectionClass === 'review-projects' && projectLastModified) ? projectLastModified : tile?.activityDate) as string)}  
                </span>
              </div>
              {!showStatus && (
                <div
                  className="truncate px-5"
                  style={{ fontSize: `${tileTextFontSize || 18}px` }}
                  data-ffid={`lbl-${cleanString(ffidCurated ?? '')}-tile-subtext-${keyProp}`}
                >
                  {tile?.tileSubtext} {showSaveToProjects && tile.tileSubtext && <span>{'-'}</span>}{' '}
                  <span data-ffid={`lbl-${cleanString(ffidCurated ?? '')}-curated-saveto-price-${keyProp}`}>
                    {tile?.price && showSaveToProjects && '$' + tile.price}
                  </span>
                </div>
              )}
              {showExpiresIn && (
                <div
                  className={`${fontWeightMap[expiresInOrStatusFontweight ?? 'regular']},truncate px-5`}
                  style={{
                    fontSize: `${tileTextFontSize || 18}px`,
                    background: `${expiresInOrStatusBgColor}`,
                    color: expiresFontColor,
                  }}
                  data-ffid={`lbl-${cleanString(ffidCurated ?? '')}-tile-status-${keyProp}`}
                >
                  {expiresInOrStatus}
                  {showStatus ? (
                    <span data-ffid={`lbl-subtext-${cleanString(ffidCurated ?? '')}-status`}> {' '} {tile?.tileSubtext} </span>
                  ) : (
                    <span>{tile?.activityDate ? ` ${findExpirationDuration(tile.activityDate, activityDateText)}` : ''}</span>
                  )}
                </div>
              )}
            </div>
            <div
              className={`flex items-center justify-center ${isPureMobile && 'items-stretch'} mt-16 lg:mt-12`}
              data-ffid={`ctr-${cleanString(ffidCurated ?? '')}-tile-actions-${keyProp}`}
            >
              {showSaveToProjects && !isDesktopSize && (
                <SaveToProjectsComponent
                  ffidCurated={ffidCurated}
                  expiresInOrStatus={expiresInOrStatus}
                  themeColor={themeColor}
                  keyProp={`${keyProp}`}
                />
              )}

              <div
                className={clsx(
                  `rounded-2xl px-16 ${isDesktopSize ? '' : 'ml-12'} inline-block`,
                  isPureMobile && showSaveToProjects ? 'text-16 leading-8' : 'lg:text-16',
                  sectionClass === 'past-order' && 'after:absolute after:inset-0',
                )}
                style={
                  themeColor == 'dark'
                    ? {
                        color: `${WHITE_COLOR_HEX}`,
                      }
                    : {
                        color: BLUE_LINK_COLOR_HEX,
                      }
                }
                data-ffid={`btn-${cleanString(ffidCurated ?? '')}-tile-cta-${keyProp}`}
              >
                {sectionClass === 'past-order' ? (
                  <span className="flex items-center" data-ffid={`lbl-past-ord-details-${keyProp}`}>
                    {tileCtaText}
                    <RightArrowIcon
                      className="text-white"
                      data-ffid={`icon-right-${cleanString(ffidCurated ?? '')}-Edit`}
                    />
                  </span>
                ) : (
                  <button
                    type="button"
                    className="m-0 flex items-center border-0 p-0"
                    onClick={() => {
                      void handleEdit(tile?.item);
                    }}
                  >
                    {tileCtaText}
                    <RightArrowIcon
                      className="text-white"
                      data-ffid={`icon-right-${cleanString(ffidCurated ?? '')}-Edit`}
                    />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default Tile;
